import '@forest/native-diagnostics'

import {
  getProducts,
  getReceiptSystemAccountIdSafe,
  getStoreRegionSafe,
  initializeNativeBridge,
  native,
  type Products,
} from '@forest/native-services'
import { fetchTrialReminderGroup } from './features/multi-page/lib/trial-reminder'
import './lib/posthog'
import {
  pageVisibleOnce,
  regionReady,
  serviceLevelStatusReady,
  trialReminderGroupReady,
} from './loading'
import { defineCustomClientStrategy } from './paraglide/runtime'
import { appVersion, deviceRegion, language, platform } from './params'
import { api } from './services/api'
import { setProducts } from './store/products'
import { $receiptSystemAccountId } from './store/receipt-system-account-id'
import { $serviceLevelStatus } from './store/service-level-status'
import { userInfoSource } from './store/user-info'
import type { ServiceLevelStatus } from './types/service-level'

defineCustomClientStrategy('custom-queryParam', {
  getLocale: () => language,
  setLocale: () => {},
})

initializeNativeBridge({
  bifrost: { enableDebug: import.meta.env.MODE !== 'production' },
  // Version-gated services (l10n, experiment, notification) read this.
  appVersion,
  onPageVisible: () => pageVisibleOnce.done(),
})

void getReceiptSystemAccountIdSafe(appVersion).then((id) => {
  $receiptSystemAccountId.set(id)
})

const userInfoPromise = userInfoSource.getReady()
const productsPromise = getProducts({
  cacheKeyPrefix: 'trialPaywall.products.v2',
  api,
  appVersion,
  platform,
  deferredUserInfo: userInfoPromise,
  env: import.meta.env.MODE as 'production' | 'staging' | 'dev',
  shouldMock: import.meta.env.DEV,
})

function productIdsOf(products: Products): string {
  return [
    products.annualOriginal.product_id,
    products.annualEarlyBird.product_id,
    products.monthlyOriginal.product_id,
    products.monthlyEarlyBird.product_id,
  ]
    .concat(products.monthlyNoTrial ? [products.monthlyNoTrial.product_id] : [])
    .sort((a, b) => a - b)
    .join(',')
}

if (import.meta.env.DEV) {
  pageVisibleOnce.done()
  // No native bridge locally: preview the experiment's Variant1 page.
  trialReminderGroupReady.done('Variant1')
  const devServiceLevelStatus = {
    service_level: 'signup' as const,
    is_premium: false,
  }
  $serviceLevelStatus.set(devServiceLevelStatus)
  serviceLevelStatusReady.done(devServiceLevelStatus)

  if (deviceRegion) {
    regionReady.done(deviceRegion)
  } else {
    getStoreRegionSafe(appVersion).then((storeRegion) => {
      regionReady.done(storeRegion)
    })
  }

  productsPromise
    .then((products) => {
      setProducts(products)
      native.general.logEvent({
        event_name: 'plus_price_test_config',
        event_params: { product_ids: productIdsOf(products) },
      })
    })
    .catch(() => {
      native.general.close()
    })
} else {
  // Asked for at app start alongside products and service-level status, but
  // nothing here awaits it: only the reminder page suspends on the arm, so
  // the first two pages (and pageReady) never wait for the experiment.
  void fetchTrialReminderGroup(native.experiment.getGroup).then((group) => {
    trialReminderGroupReady.done(group)
  })

  ;(async function getData() {
    try {
      const userInfo = await userInfoPromise

      if (deviceRegion) {
        regionReady.done(deviceRegion)
      } else {
        getStoreRegionSafe(appVersion).then((storeRegion) => {
          regionReady.done(storeRegion)
        })
      }

      await Promise.all([
        productsPromise.then((products) => {
          setProducts(products)
          native.general.logEvent({
            event_name: 'plus_price_test_config',
            event_params: { product_ids: productIdsOf(products) },
          })
        }),
        api.service
          .get(`user/users/${userInfo.user_id}/service-levels/status`)
          .json<ServiceLevelStatus>()
          .then((serviceLevelStatus) => {
            $serviceLevelStatus.set(serviceLevelStatus)
            serviceLevelStatusReady.done(serviceLevelStatus)
          }),
      ])

      native.general.pageReady()
    } catch {
      native.general.close()
    }
  })()
}
